import { useState, type FormEvent } from 'react';

const SUBMIT_URL = 'https://readdy.ai/api/form/dah8193oh653ivfvnnkg';

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

interface SubscribeFormProps {
  variant?: 'dark' | 'light';
}

export default function SubscribeForm({ variant = 'dark' }: SubscribeFormProps) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const isDark = variant === 'dark';

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const guard = String(formData.get('website_alt') ?? '').trim();
    if (guard) {
      setStatus('success');
      setMessage('订阅成功，感谢你的关注！');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      const text = String(value).trim();
      if (text) payload.append(key, text);
    });

    setStatus('loading');
    setMessage('正在提交…');
    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        setMessage('订阅成功！每周更新汇总会发送到你的邮箱。');
        form.reset();
      } else {
        setStatus('error');
        setMessage(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setMessage('网络异常，请稍后重试。');
    }
  };

  return (
    <form
      id="subscribe-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="w-full"
      noValidate
    >
      <div className="flex flex-col sm:flex-row gap-2.5">
        <label className="sr-only" htmlFor="subscribe-email">
          邮箱地址
        </label>
        <input
          id="subscribe-email"
          type="email"
          name="email"
          required
          placeholder="输入邮箱，每周一封更新汇总"
          className={`flex-1 min-w-0 px-4 py-3 rounded-md text-sm outline-none transition-colors ${
            isDark
              ? 'bg-secondary-900 text-secondary-50 placeholder:text-secondary-300 border border-secondary-700 focus:border-accent-400'
              : 'bg-background-50 text-foreground-900 placeholder:text-foreground-400 border border-background-300 focus:border-primary-400'
          }`}
        />
        <input
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="form-guard-field"
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          className="shrink-0 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
        >
          {status === 'loading' ? '提交中…' : '立即订阅'}
        </button>
      </div>
      {status !== 'idle' && status !== 'loading' ? (
        <p
          role="status"
          className={`mt-2.5 text-xs ${
            status === 'success' ? 'text-accent-300' : 'text-primary-300'
          }`}
        >
          {message}
        </p>
      ) : null}
    </form>
  );
}